/* ============================================================================
   PARA QUIÉN SE VUELA

   POR QUÉ EXISTE

   El informe de horas tiene cuatro cortes y dos de ellos —por empresa y por
   cliente— salían vacíos: el asiento del libro no decía para quién se voló. El
   catálogo de clientes ya está (e138); esto es lo que lo conecta con el vuelo.

   ═══ POR QUÉ ES UN SELECTOR Y NO UN CAMPO DE TEXTO ═══

   El corte agrupa por `clienteId`, y un nombre escrito a mano no se puede
   agrupar: «Minera Andes», «minera andes» y «Minera And.» serían tres clientes
   con las horas partidas. Elegir de una lista es lo que hace que el informe
   tenga filas en vez de renglones parecidos.

   ═══ POR QUÉ EL CLIENTE DEPENDE DE LA EMPRESA ═══

   Los clientes son de una empresa, así que la lista se filtra por la empresa
   elegida. Y al revés: **elegir un cliente dice de qué empresa es el vuelo**.
   Por eso cambiar de empresa **limpia el cliente**: dejarlo sería un par que se
   contradice, y ese asiento se contaría entero en la empresa equivocada.

   ═══ LO QUE SE DICE CUANDO NO HAY NADA QUE ELEGIR ═══

   Una empresa sin clientes cargados no deja un selector vacío y mudo: dice dónde
   se cargan. Un control vacío sin explicación se lee como una función que no
   anda, y este es el único dato del asiento que no se mide.
   ========================================================================== */
import { clientesDe } from "../../domain/clientes.js";
import "./selectorDeOperacion.css";

export default function SelectorDeOperacion({
  empresas = [],
  clientes = [],
  empresaId = null,
  clienteId = null,
  alCambiar,
  motivo = null,
  leyenda = "Para quién se vuela",
}) {
  const activas = empresas.filter((e) => e && e.activo !== false);
  const elegida = activas.find((e) => e.id === empresaId) || null;
  const suyos = clientesDe(clientes, empresaId);
  const hayCliente = suyos.some((c) => c.id === clienteId);

  const cambiar = (cambios) => alCambiar?.({ empresaId, clienteId, ...cambios });

  return (
    <fieldset className="hc-operacion">
      <legend className="hc-operacion__leyenda">{leyenda}</legend>

      {/* Con una sola empresa no hay nada que elegir: se dice cuál es. Un
          desplegable de una sola opción hace pensar que falta cargar algo. */}
      {activas.length > 1 ? (
        <label className="hc-operacion__campo">
          <span>Empresa</span>
          <select
            value={empresaId || ""}
            aria-label="Empresa del vuelo"
            onChange={(e) => cambiar({ empresaId: e.target.value || null, clienteId: null })}
          >
            <option value="">(sin empresa)</option>
            {activas.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
        </label>
      ) : (
        <span className="hc-operacion__fija">
          {activas[0]?.nombre || "Sin empresa cargada"}
        </span>
      )}

      <label className="hc-operacion__campo">
        <span>Cliente</span>
        <select
          value={hayCliente ? clienteId : ""}
          aria-label="Cliente del vuelo"
          onChange={(e) => cambiar({ clienteId: e.target.value || null })}
        >
          {/* El vuelo sin cliente NO es un dato faltante: un traslado propio o
              una instrucción no son para nadie, y contarlos como «falta el
              cliente» ensuciaría el informe con un problema que no existe. */}
          <option value="">(sin cliente: traslado propio o instrucción)</option>
          {suyos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
      </label>

      {suyos.length === 0 && (
        <p className="hc-operacion__nota">
          {elegida
            ? `${elegida.nombre} todavía no tiene clientes cargados. Se cargan en Administración → Clientes; mientras tanto, los vuelos quedan sin cliente.`
            : "No hay ninguna empresa cargada: el vuelo va a quedar sin decir para quién se voló."}
        </p>
      )}

      {/* El rechazo va acá, pegado al control que lo causó. En un aviso lejano
          se lee como que el botón no hizo nada. */}
      {motivo && <p className="hc-operacion__motivo" role="status">{motivo}</p>}
    </fieldset>
  );
}
